
import { Vue, Options, prop } from "vue-class-component";
import i18n from "@/i18n";
import { HandoverController } from "@/app/ui/controllers/HandoverController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { convertDecimalWithComma } from "@/app/infrastructures/misc/Utils";
import { Pagination } from "@/domain/entities/Pagination";
import { RequestFailedVendorStt } from "@/data/payload/api/HandoverApiRequest";
import {
  FailedVendorStt,
  FailedVendorSttItem,
  HandoverSttResult
} from "@/domain/entities/Handover";
import {
  resolveFailedVendorSttReason,
  getFailedVendorColumns
} from "./vendorFailedSttHelper";
import copy from "copy-text-to-clipboard";
import Notification from "@/app/ui/components/notification/index.vue";

class Props {
  handoverId = prop<number>({
    default: 0,
    type: Number
  });
  eligibleRetry = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  name: "VendorStatusFailedModal",
  components: {
    Notification
  },
  emits: ["close", "retry"]
})
class VendorStatusFailedModal extends Vue.with(Props) {
  pagination = new Pagination(1, 100, 0);
  isFetchingMore = false;
  isInitialLoaded = false;
  observer: IntersectionObserver | null = null;

  mounted() {
    this.init();
    this.attachScrollListener();
  }

  beforeUnmount() {
    this.destroyObserver();
    this.detachScrollListener();
    this.resetData();
  }

  attachScrollListener() {
    if (typeof window === "undefined") return;
    window.addEventListener("scroll", this.onScroll, {
      capture: true,
      passive: true
    });
    this.$nextTick(() => {
      this.bindLocalScroll();
    });
  }

  get scrollContainer(): HTMLElement | null {
    if (typeof document === "undefined") return null;
    return (
      (this.$refs?.scrollContainer as HTMLElement | null) ||
      document.getElementById("handover-failed-vendor-scroll-container") ||
      (this.$el?.querySelector?.(
        "[data-testid='handover-failed-vendor-scroll-container']"
      ) as HTMLElement | null) ||
      document.querySelector(
        "[data-testid='handover-failed-vendor-scroll-container']"
      ) ||
      null
    );
  }

  get sentinelElement(): HTMLElement | null {
    if (typeof document === "undefined") return null;
    return (
      (this.$refs?.sentinel as HTMLElement | null) ||
      document.getElementById("handover-failed-vendor-sentinel") ||
      (this.$el?.querySelector?.(
        "[data-testid='handover-failed-vendor-sentinel']"
      ) as HTMLElement | null) ||
      document.querySelector(
        "[data-testid='handover-failed-vendor-sentinel']"
      ) ||
      null
    );
  }

  bindLocalScroll() {
    const container = this.scrollContainer;
    if (!container) return;
    container.addEventListener("scroll", this.onScroll, { passive: true });
    const tableParent = container.querySelector(
      "#table-parent"
    ) as HTMLElement | null;
    tableParent?.addEventListener("scroll", this.onScroll, { passive: true });
  }

  detachScrollListener() {
    if (typeof window !== "undefined") {
      window.removeEventListener("scroll", this.onScroll, {
        capture: true
      } as any);
    }
    const container = this.scrollContainer;
    container?.removeEventListener("scroll", this.onScroll);
    const tableParent = container?.querySelector(
      "#table-parent"
    ) as HTMLElement | null;
    tableParent?.removeEventListener("scroll", this.onScroll);
  }

  async init() {
    this.destroyObserver();
    this.resetData();
    // Fetch failed-vendor-stt (cached in failedVendorSttByHandoverId) and
    // the handover detail for package-meta enrichment. Detail is best-effort:
    // the table still renders STT numbers if it fails.
    try {
      await Promise.all([
        HandoverController.getFailedVendorStt({
          handoverId: this.handoverId,
          params: new RequestFailedVendorStt({
            page: 1,
            limit: 100,
            isTotalData: true,
            showOnlyFailed: true
          }),
          append: false
        }),
        HandoverController.getDetailHandover(this.handoverId)
      ]);
      this.pagination.totalData = this.failedVendorStt.pagination.totalData;
    } catch {
      this.close();
      return;
    }
    this.$nextTick(() => {
      const container = this.scrollContainer;
      if (container) container.scrollTop = 0;
      const tableParent = container?.querySelector(
        "#table-parent"
      ) as HTMLElement | null;
      if (tableParent) tableParent.scrollTop = 0;
      this.isInitialLoaded = true;
      this.bindLocalScroll();
      this.initObserver();
    });
  }

  get hasMore(): boolean {
    const total = this.pagination.totalData;
    if (total != null && total > 0) {
      return this.failedSttList.length < total;
    }
    return (
      this.failedSttList.length >= this.pagination.page * this.pagination.limit
    );
  }

  get isLoading(): boolean {
    return HandoverController.isLoadingFailedVendorStt;
  }

  get isLoadingInitial(): boolean {
    return this.isLoading && this.pagination.page === 1;
  }

  get isLoadingMore(): boolean {
    return (this.isLoading || this.isFetchingMore) && this.pagination.page > 1;
  }

  onScroll = (event?: Event) => {
    this.checkScrollEnd(event?.target as HTMLElement | undefined);
  };

  checkScrollEnd = (scrolledTarget?: HTMLElement) => {
    if (
      !this.isInitialLoaded ||
      this.isLoading ||
      this.isFetchingMore ||
      !this.hasMore
    ) {
      return;
    }

    const container = this.scrollContainer;
    const tableParent = container?.querySelector(
      "#table-parent"
    ) as HTMLElement | null;

    const candidates: HTMLElement[] = [];
    if (
      scrolledTarget &&
      (scrolledTarget === container ||
        container?.contains(scrolledTarget) ||
        scrolledTarget.contains?.(container ?? document.body))
    ) {
      candidates.push(scrolledTarget);
    }
    if (container && !candidates.includes(container)) {
      candidates.push(container);
    }
    if (tableParent && !candidates.includes(tableParent)) {
      candidates.push(tableParent);
    }

    for (const el of candidates) {
      const isVerticallyScrollable = el.scrollHeight > el.clientHeight + 10;
      if (isVerticallyScrollable && el.scrollTop > 0) {
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
          this.loadMore();
          break;
        }
      }
    }
  };

  initObserver() {
    if (typeof IntersectionObserver === "undefined") return;
    this.destroyObserver();
    const scrollContainer = this.scrollContainer;
    const sentinel = this.sentinelElement;
    if (!scrollContainer || !sentinel) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            this.isInitialLoaded &&
            (scrollContainer.scrollTop || 0) > 0
          ) {
            this.loadMore();
          }
        });
      },
      {
        root: scrollContainer,
        rootMargin: "100px",
        threshold: 0.1
      }
    );
    this.observer.observe(sentinel);
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  async loadMore() {
    if (
      !this.isInitialLoaded ||
      this.isLoading ||
      this.isFetchingMore ||
      !this.hasMore
    ) {
      return;
    }
    this.isFetchingMore = true;
    const nextPage = this.pagination.page + 1;
    try {
      this.pagination.page = nextPage;
      const res = await HandoverController.getFailedVendorStt({
        handoverId: this.handoverId,
        params: new RequestFailedVendorStt({
          page: nextPage,
          limit: this.pagination.limit,
          isTotalData: true
        }),
        append: true
      });
      this.pagination.totalData = res.pagination.totalData;
      this.$nextTick(() => {
        this.bindLocalScroll();
        this.checkScrollEnd();
      });
    } catch {
      this.pagination.page = Math.max(1, nextPage - 1);
    } finally {
      this.isFetchingMore = false;
    }
  }

  close() {
    this.destroyObserver();
    this.detachScrollListener();
    this.resetData();
    this.$emit("close");
  }

  get failedVendorStt(): FailedVendorStt {
    return (
      HandoverController.failedVendorSttByHandoverId[this.handoverId] ||
      new FailedVendorStt()
    );
  }

  get failedSttList() {
    const fallbackReason = this.translate(
      "handover.list.failedVendorModal.defaultReason"
    );
    return this.failedVendorStt.stt.map((item: FailedVendorSttItem) => {
      const meta = this.detailBySttNo[item.sttNo];
      const reason = resolveFailedVendorSttReason(
        item.reason,
        item.message,
        fallbackReason
      );
      const reasonEn = resolveFailedVendorSttReason(
        item.reasonEn,
        item.message,
        fallbackReason
      );
      return {
        sttNo: item.sttNo,
        product: meta?.sttProductType || "—",
        commodity: meta ? this.commodityName(meta) : "—",
        pieces: meta?.sttTotalPiece ?? "—",
        grossWeight: meta
          ? `${convertDecimalWithComma(meta.sttGrossWeight, 2)} Kg`
          : "—",
        volumeWeight: meta
          ? `${convertDecimalWithComma(meta.sttVolumeWeight, 2)} Kg`
          : "—",
        reason,
        reasonEn
      };
    });
  }

  get detailBySttNo(): Record<string, HandoverSttResult> {
    const map: Record<string, HandoverSttResult> = {};
    HandoverController.detailHandover.stt.forEach((item: HandoverSttResult) => {
      map[item.sttNo] = item;
    });
    return map;
  }

  commodityName(meta: HandoverSttResult) {
    return AccountController.accountData.accountIsForeign
      ? meta.sttCommodityNameEn
      : meta.sttCommodityName;
  }

  translate(key: string, values?: any): string {
    if (typeof this.$t === "function") {
      return this.$t(key, values);
    }
    const globalT = (i18n?.global as any)?.t;
    if (typeof globalT === "function") {
      return globalT(key, values);
    }
    return key;
  }

  get title() {
    return this.translate("handover.list.failedVendorModal.title", {
      n: this.pagination.totalData ?? this.failedSttList.length
    });
  }

  get columns() {
    return getFailedVendorColumns((key: string) => this.translate(key));
  }

  retry() {
    this.$emit("retry", this.handoverId);
  }

  isCopied = false;

  copyReason(reason: string) {
    if (!reason || reason === "—" || reason === "-") return;
    const success = copy(reason);
    if (success) {
      this.isCopied = false;
      this.$nextTick(() => {
        this.isCopied = true;
      });
    }
  }

  get copyStyles() {
    return {
      background: "bg-gray-lp-300",
      text: "text-white",
      message: this.translate("handover.list.failedVendorModal.copiedReason"),
      width: "w-300px"
    };
  }

  resetData() {
    this.isInitialLoaded = false;
    this.pagination.page = 1;
    this.pagination.totalData = 0;
    HandoverController.clearFailedVendorStt(this.handoverId);
    const container = this.scrollContainer;
    if (container) {
      container.scrollTop = 0;
    }
    const tableParent = container?.querySelector(
      "#table-parent"
    ) as HTMLElement | null;
    if (tableParent) {
      tableParent.scrollTop = 0;
    }
  }

  get isForeignAccount() {
    return AccountController.accountData.accountIsForeign;
  }
}
export default VendorStatusFailedModal;
